import { z } from 'zod';
import { nutritionLabelNutrientsSchema, nutritionSummarySchema } from "./food";

export const SERVER_MODIFIER_ID = 'server' as const;
export const DEVICE_MODIFIER_PREFIX = 'device:' as const;

export const toDeviceModifierId = (deviceId: string): string =>
  `${DEVICE_MODIFIER_PREFIX}${deviceId}`;

export const diaryExternalSourceSchema = z.object({
  provider: z.literal('intervals_icu'),
  id: z.string().trim().min(1),
  suppressedAt: z.string().optional(),
});
export type DiaryExternalSource = z.infer<typeof diaryExternalSourceSchema>;

export const diaryExternalSourceSuppressionSchema = z.object({
  provider: z.literal('intervals_icu'),
  entityType: z.enum(['event', 'activity']),
  id: z.string().trim().min(1),
  parentId: z.string().trim().min(1).optional(),
  suppressedAt: z.string(),
});
export type DiaryExternalSourceSuppression = z.infer<
  typeof diaryExternalSourceSuppressionSchema
>;

export const diaryDayGoalSnapshotSchema = z.object({
  goalType: z.enum(['lose', 'maintain', 'gain', 'fixed']),
  caloriesTarget: z.number(),
  proteinG: z.number(),
  carbsG: z.number(),
  fatG: z.number(),
  flexMacro: z.enum(['carbs', 'fat']).nullable().optional(),
  sourceGoalId: z.string(),
  resolvedAt: z.string(),
});
export type DiaryDayGoalSnapshot = z.infer<typeof diaryDayGoalSnapshotSchema>;

// ---------------------------------------------------------------------------
// Logged foods (entries) now live INSIDE a meal (`meal.entries[]`). The
// containing meal is the sole association — there is no top-level `entries[]`
// and no `mealId`/`mealPlanId` back-reference on the entry itself.
// ---------------------------------------------------------------------------
export const diaryDayEntrySchema = z.object({
  id: z.string(),
  externalSource: diaryExternalSourceSchema.optional(),
  loggedAt: z.string(),
  serving: z.object({
    amount: z.number(),
    unit: z.string(),
  }),
  nutritionSnapshot: nutritionSummarySchema,
  source: z.object({
    type: z.enum(['system', 'custom', 'recipe', 'ai', 'exercise']),
    label: z.string().optional(),
    foodId: z.string().optional(),
    recipeId: z.string().optional(),
    exerciseTypeId: z.string().optional(),
  }),
  exercise: z.object({
    durationMinutes: z.number(),
    intensity: z.enum(['low', 'medium', 'high']),
  }).optional(),
  createdAt: z.string(),
});
export type DiaryDayEntry = z.infer<typeof diaryDayEntrySchema>;
export type DiaryDayEntrySourceType = DiaryDayEntry['source']['type'];
export type DiaryDayEntryIntensity = NonNullable<DiaryDayEntry['exercise']>['intensity'];

export const diaryMealPlanKindSchema = z.enum(['meal', 'workout', 'meal-with-workout']);
export type DiaryMealPlanKind = z.infer<typeof diaryMealPlanKindSchema>;

// ---------------------------------------------------------------------------
// Target / exercise shapes shared by the `planned` baseline (fully specified)
// and the `custom` override (partial — only user-entered fields).
// ---------------------------------------------------------------------------
export const diaryMealTargetSchema = z.object({
  calories: z.number(),
  protein: z.number(),
  carbs: z.number(),
  fat: z.number(),
});
export type DiaryMealTarget = z.infer<typeof diaryMealTargetSchema>;

export const diaryMealExerciseSchema = z.object({
  durationMinutes: z.number().nullable().optional(),
  intensity: z.enum(['low', 'medium', 'high']).nullable(),
  caloriesBurned: z.number().nullable(),
  carbsPerHour: z.number().nullable().optional(),
  carbsConsumed: z.number().nullable(),
  caloriesConsumed: z.number().nullable(),
});
export type DiaryMealExercise = z.infer<typeof diaryMealExerciseSchema>;

export const diaryMealPlannedSchema = z.object({
  title: z.string(),
  plannedAt: z.string(),
  target: diaryMealTargetSchema.nullable(),
  exercise: diaryMealExerciseSchema.nullable(),
});
export type DiaryMealPlanned = z.infer<typeof diaryMealPlannedSchema>;

// Partial override. Presence of a field == the user set it, and it wins over
// `planned` permanently until reset. `null` is an explicit user value (e.g.
// "no exercise duration"), distinct from an absent key (planner-controlled).
export const diaryMealCustomSchema = z.object({
  title: z.string().optional(),
  plannedAt: z.string().optional(),
  // A completion time the user has established. Preserved across an
  // uncomplete → re-complete cycle so re-completing keeps the prior time
  // instead of snapping back to "now".
  completedAt: z.string().optional(),
  target: z.object({
    calories: z.number().optional(),
    protein: z.number().optional(),
    carbs: z.number().optional(),
    fat: z.number().optional(),
  }).optional(),
  exercise: z.object({
    durationMinutes: z.number().nullable().optional(),
    intensity: z.enum(['low', 'medium', 'high']).nullable().optional(),
    caloriesBurned: z.number().nullable().optional(),
    carbsPerHour: z.number().nullable().optional(),
    carbsConsumed: z.number().nullable().optional(),
    caloriesConsumed: z.number().nullable().optional(),
  }).optional(),
  // Corrections the athlete made to a completed workout's recorded actuals.
  // Kept apart from `plannedAt` / `exercise` (planned-phase edits): when an
  // external activity (intervals.icu) completes the workout, its actual values
  // supersede the planned edits, but never these.
  actuals: z.object({
    startedAt: z.string().optional(),
    durationMinutes: z.number().optional(),
    intensity: z.enum(['low', 'medium', 'high']).nullable().optional(),
    caloriesBurned: z.number().optional(),
  }).optional(),
});
export type DiaryMealCustom = z.infer<typeof diaryMealCustomSchema>;

export const diaryMealPeriodizationSchema = z.object({
  strategy: z.enum(['equal']),
  mode: z.enum(['simple', 'detail']),
  simpleCarbLevel: z.enum(['low', 'medium', 'high']).nullable(),
  generatedWeights: z.object({
    calories: z.number(),
    protein: z.number(),
    carbs: z.number(),
    fat: z.number(),
  }),
  macroRatios: z.object({
    calories: z.number(),
    protein: z.number(),
    carbs: z.number(),
    fat: z.number(),
  }),
});
export type DiaryMealPeriodization = z.infer<typeof diaryMealPeriodizationSchema>;

export const diaryWorkoutActualsSchema = z.object({
  durationMinutes: z.number(),
  intensity: z.enum(['low', 'medium', 'high']).nullable(),
  caloriesBurned: z.number(),
  fuelCarbsPerHour: z.number().nullable(),
  fuelCalories: z.number(),
  fuelCarbs: z.number(),
  completedAt: z.string(),
});
export type DiaryWorkoutActuals = z.infer<typeof diaryWorkoutActualsSchema>;

export const diaryWorkoutSupportSnackSchema = z.object({
  workoutPlanId: z.string(),
  timing: z.enum(['pre', 'post']),
});
export type DiaryWorkoutSupportSnack = z.infer<typeof diaryWorkoutSupportSnackSchema>;

export const diaryWorkoutSupportSnackSuppressionSchema = z.object({
  preWorkout: z.boolean().optional(),
  postWorkout: z.boolean().optional(),
});
export type DiaryWorkoutSupportSnackSuppression = z.infer<typeof diaryWorkoutSupportSnackSuppressionSchema>;

export const diaryMealPlanPlannerResultSchema = z.object({
  mealTarget: z.object({
    calories: z.number(),
    proteinG: z.number(),
    carbsG: z.number(),
    fatG: z.number(),
    reasonCodes: z.array(z.string()),
    confidence: z.number().optional(),
  }).nullable().optional(),
  workoutFuelingTarget: z.object({
    calories: z.number(),
    carbsG: z.number(),
    carbsPerHour: z.number(),
    source: z.enum(['provided', 'calculated']).optional(),
    reasonCodes: z.array(z.string()),
  }).nullable().optional(),
  underFuelingTargeted: z.boolean().nullable().optional(),
});
export type DiaryMealPlanPlannerResult = z.infer<typeof diaryMealPlanPlannerResultSchema>;

// ---------------------------------------------------------------------------
// A meal is the unit the whole diary day is built from. It carries plan info,
// plan details (planner-written `planned` baseline + sticky partial `custom`
// overrides), workout lifecycle, and its own logged foods (`entries`).
// ---------------------------------------------------------------------------
export const diaryMealSchema = z.object({
  id: z.string(),
  externalSource: diaryExternalSourceSchema.optional(),
  kind: diaryMealPlanKindSchema.optional(),
  mealId: z.number().nullable(),
  planned: diaryMealPlannedSchema,
  custom: diaryMealCustomSchema.optional(),
  periodization: diaryMealPeriodizationSchema.optional(),
  plannerResult: diaryMealPlanPlannerResultSchema.nullable().optional(),
  workoutActuals: diaryWorkoutActualsSchema.nullable().optional(),
  workoutSupportSnack: diaryWorkoutSupportSnackSchema.nullable().optional(),
  workoutSupportSnackSuppression: diaryWorkoutSupportSnackSuppressionSchema.optional(),
  entries: z.array(diaryDayEntrySchema).default([]),
  completedAt: z.string().nullable().optional(),
  skippedAt: z.string().nullable().optional(),
  deletedAt: z.string().nullable().optional(),
});
export type DiaryMeal = z.infer<typeof diaryMealSchema>;

// ---------------------------------------------------------------------------
// Resolver helpers — the single source of truth for "effective" meal values.
// Effective = custom.<field> when present, else planned.<field>.
// ---------------------------------------------------------------------------
export const resolveMealTitle = (meal: DiaryMeal): string =>
  meal.custom?.title ?? meal.planned.title;

export const resolveMealPlannedAt = (meal: DiaryMeal): string =>
  meal.custom?.plannedAt ?? meal.planned.plannedAt;

export const resolveMealTarget = (meal: DiaryMeal): DiaryMealTarget | null => {
  const planned = meal.planned.target;
  const custom = meal.custom?.target;
  if (!planned && !custom) return null;
  return {
    calories: custom?.calories ?? planned?.calories ?? 0,
    protein: custom?.protein ?? planned?.protein ?? 0,
    carbs: custom?.carbs ?? planned?.carbs ?? 0,
    fat: custom?.fat ?? planned?.fat ?? 0,
  };
};

export const resolveMealExercise = (meal: DiaryMeal): DiaryMealExercise | null => {
  const planned = meal.planned.exercise;
  const custom = meal.custom?.exercise;
  if (!planned && !custom) return null;
  // A custom key that is explicitly present (including `null`) wins over planned.
  const pick = <K extends keyof DiaryMealExercise>(key: K): DiaryMealExercise[K] =>
    custom && key in custom
      ? (custom[key as keyof typeof custom] as DiaryMealExercise[K])
      : ((planned?.[key] as DiaryMealExercise[K]) ?? null);
  return {
    durationMinutes: pick('durationMinutes'),
    intensity: pick('intensity'),
    caloriesBurned: pick('caloriesBurned'),
    carbsPerHour: pick('carbsPerHour'),
    carbsConsumed: pick('carbsConsumed'),
    caloriesConsumed: pick('caloriesConsumed'),
  };
};

export const isMealCustomized = (meal: DiaryMeal): boolean => {
  const custom = meal.custom;
  if (!custom) return false;
  return (
    custom.title !== undefined ||
    custom.plannedAt !== undefined ||
    (custom.target !== undefined && Object.keys(custom.target).length > 0) ||
    (custom.exercise !== undefined && Object.keys(custom.exercise).length > 0) ||
    (custom.actuals !== undefined && Object.keys(custom.actuals).length > 0)
  );
};

export const diaryPlannerCostTermSchema = z.object({
  id: z.string(),
  label: z.string(),
  group: z.string(),
  groupLabel: z.string(),
  variable: z.string(),
  unit: z.string(),
  kind: z.enum(["target", "slack"]),
  context: z.object({
    date: z.string().optional(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    mealId: z.string().optional(),
    mealIds: z.array(z.string()).optional(),
    workoutId: z.string().optional(),
    fuelingWindowId: z.string().optional(),
    required: z.number().optional(),
  }).optional(),
  weight: z.number(),
  target: z.number(),
  actual: z.number(),
  delta: z.number(),
  contribution: z.number(),
});
export type DiaryPlannerCostTerm = z.infer<typeof diaryPlannerCostTermSchema>;

export const diaryPlannerCostGroupSchema = z.object({
  id: z.string(),
  label: z.string(),
  contribution: z.number(),
  termCount: z.number(),
});
export type DiaryPlannerCostGroup = z.infer<typeof diaryPlannerCostGroupSchema>;

export const diaryPlannerCostEquationSchema = z.object({
  formula: z.string(),
  total: z.number(),
  solverObjectiveValue: z.number().optional(),
  groups: z.array(diaryPlannerCostGroupSchema),
  terms: z.array(diaryPlannerCostTermSchema),
});
export type DiaryPlannerCostEquation = z.infer<typeof diaryPlannerCostEquationSchema>;

export const diaryPlannerDiagnosticsSchema = z.object({
  solved: z.boolean(),
  solverStatus: z.string(),
  statusCode: z.number().optional(),
  objectiveValue: z.number().optional(),
  iterations: z.number().optional(),
  solveTimeMs: z.number().optional(),
  setupTimeMs: z.number().optional(),
  variableCount: z.number().optional(),
  constraintCount: z.number().optional(),
  costEquation: diaryPlannerCostEquationSchema.optional(),
});
export type DiaryPlannerDiagnostics = z.infer<typeof diaryPlannerDiagnosticsSchema>;

export const diaryDayImportedTotalsSchema = z.object({
  importSource: z.enum(['chronometer', 'manual']),
  calories: z.number(),
  protein: z.number(),
  carbs: z.number(),
  fat: z.number(),
  labelNutrients: nutritionLabelNutrientsSchema.nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type DiaryDayImportedTotals = z.infer<typeof diaryDayImportedTotalsSchema>;

export const diaryDayPlanGenerationSchema = z.object({
  generatedForDay: z.boolean().default(false),
  generatedAt: z.string().nullable().default(null),
  lastInputHash: z.string().nullable().default(null),
  status: z.enum(['idle', 'pending', 'error']).default('idle'),
  errorMessage: z.string().nullable().default(null),
  plannerDiagnostics: diaryPlannerDiagnosticsSchema.nullable().default(null),
});
export type DiaryDayPlanGeneration = z.infer<typeof diaryDayPlanGenerationSchema>;
export type DiaryDayPlanGenerationStatus = DiaryDayPlanGeneration['status'];

export const diaryDayTrainingLabelSchema = z.enum([
  'Rest',
  'Recovery',
  'Endurance',
  'Threshold',
  'Long',
  'Race',
]);
export type DiaryDayTrainingLabel = z.infer<typeof diaryDayTrainingLabelSchema>;

export const diaryDayBodySchema = z.object({
  goalSnapshot: diaryDayGoalSnapshotSchema.nullable(),
  meals: z.array(diaryMealSchema).default([]),
  // Optional coach/athlete override for the derived training day-type label.
  trainingLabel: diaryDayTrainingLabelSchema.nullable().optional(),
  externalSourceSuppressions: z.array(diaryExternalSourceSuppressionSchema).optional(),
  importedTotals: diaryDayImportedTotalsSchema.nullable(),
  planGeneration: diaryDayPlanGenerationSchema.default({
    generatedForDay: false,
    generatedAt: null,
    lastInputHash: null,
    status: 'idle',
    errorMessage: null,
    plannerDiagnostics: null,
  }),
});
export type DiaryDayBody = z.infer<typeof diaryDayBodySchema>;

export const diaryDaySchema = z.object({
  id: z.string(),
  userId: z.string(),
  date: z.string(),
  body: diaryDayBodySchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  syncRevision: z.number().int().nonnegative(),
  modifiedBy: z.string().min(1).max(100),
});
export type DiaryDay = z.infer<typeof diaryDaySchema>;
export type DiaryDayDocType = DiaryDay;
